import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import { useSearchParams } from 'react-router-dom';
import { applyJobv2 } from '../../actions/jobs';
import { fetchPublicJob } from '../../actions/job';
import PreScreening from './preScreeningQuestions/preScreeningQuestions';
import FinishForm from './finishForm/finishForm';
import ErrorForm from './ErrorWindow/ErrorForm';
import UserDetail from './userDetail/userDetail';
import ReviewForm from './reviewForm/reviewForm';
import { userExistConfig } from '../../utils/constants';
import { removeNotification, setNotification } from '../../actions/appNotifications';
import UploadCV from './uploadCv/uploadCv';
import { validateUser } from '../../actions/user';
import { QUICK_APPLY_USER } from './types';
import { getSerializedSearchParams, useURLParams } from '../../utils/helpers';
import { getGuestDetails, saveGuestDetails } from '../../utils/quickApplySession';

function QuickApply() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useURLParams();
  const [step, setStep] = useState(1);
  const isAuthenticated = useIsAuthenticated();

  // Guest-only convenience: basic contact details kept for this browser session.
  // Resolved once, before the form state below is seeded from it.
  const restoredDetails = useMemo(() => (isAuthenticated ? null : getGuestDetails()), [isAuthenticated]);

  const [data, setData] = useState(() => ({
    JobId: JSON.parse(localStorage.getItem('OpenedJobId') || searchParams.get('id') || false),
    UserId: Number(localStorage.getItem('UserId')),
    firstName: restoredDetails?.firstName || '',
    lastName: restoredDetails?.lastName || '',
    email: restoredDetails?.email || '',
    countryName: restoredDetails?.countryName || '',
    PhoneCountryCode: restoredDetails?.PhoneCountryCode || '',
    Phone: restoredDetails?.Phone || '',
    IsManageConcent: false,
    Cv: undefined,
    ApplicationSource: params?.utm_source || '',
    ApplicationSourceDrillDown1: params?.utm_medium || '',
    ApplicationSourceDrillDown2: params?.utm_campaign || '',
    // eslint-disable-next-line no-underscore-dangle -- _hsenc is a HubSpot URL query param name
    HubSpotCampaign: params?._hsenc || ''
  }));
  const [error] = useState(null);
  const [questions, setQuestions] = useState({});
  const [userDataExist] = useState(false);
  const [, setUserExist] = useState(null);
  const [isUserExist, setIsUserExist] = useState(false);

  const job = useSelector((state) => state.job.response);
  const userValidation = useSelector((state) => state.user?.profile?.validation?.Data);
  const userValidationPending = useSelector((state) => state.user?.profile?.userValidationPending);
  const isJobBeingApplied = useSelector((state) => state.jobs?.pendingApplyV2);
  const isJobApplyFailed = useSelector((state) => state.jobs?.errorApplyV2);
  const isJobApplicationHasData = useSelector((state) => state.jobs?.jobapplicationv2);
  const dispatch = useDispatch();
  const questionAvailability = Object.values(questions).every((x) => x === null || x?.trim() === '');

  // The pre-screening step only exists when the job asks questions, so the
  // progress indicator counts 4 steps without it and 5 with it.
  const totalSteps = questionAvailability ? 4 : 5;
  const displayStep = questionAvailability && step > 3 ? step - 1 : step;

  const close = () => setSearchParams({ ...getSerializedSearchParams(), action: '' });

  const setConsentChecked = (consentCheck) => {
    setData((prevState) => ({
      ...prevState,
      IsManageConcent: consentCheck
    }));
  };

  const handleAnswers = (ans) => {
    switch (ans) {
      case 1:
        if (data?.Question1) return data?.Answer1 ? 'true' : 'false';
        return null;
      case 2:
        if (data?.Question2) return data?.Answer2 ? 'true' : 'false';
        return null;
      case 3:
        if (data?.Question3) return data?.Answer3 ? 'true' : 'false';
        return null;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (userValidation === 'User is already exist') {
      setUserExist('userExists');
    } else if (userValidation === 'You have already applied for a Job') {
      setUserExist('applied');
    } else {
      setUserExist(null);
    }
  }, [userValidation]);

  useEffect(() => {
    if (job && job.Id === data.JobId) {
      setQuestions({
        Question1: job.Question1,
        Question2: job.Question2,
        Question3: job.Question3
      });
    }
  }, [job, data]);

  useEffect(() => {
    if (data?.JobId) dispatch(fetchPublicJob(data?.JobId));
  }, [data?.JobId]);

  const handleNavigattion = (type) => {
    switch (type) {
      case 'contact':
        setStep(1);
        break;
      case 'attachment':
        setStep(2);
        break;
      case 'answers':
        setStep(3);
        break;
      default:
        setStep(1);
    }
  };

  useEffect(() => {
    if (
      isJobBeingApplied === false &&
      isJobApplyFailed === false &&
      isJobApplicationHasData &&
      data?.JobId === isJobApplicationHasData.JobId
    ) {
      if (!isAuthenticated) saveGuestDetails(data);
      setStep(5);
    }
  }, [isJobBeingApplied, isJobApplyFailed, isJobApplicationHasData]);

  switch (step) {
    case 1:
      return (
        <UserDetail
          step={displayStep}
          totalStep={totalSteps}
          userValidationPending={userValidationPending}
          nextHandle={(newData) => {
            dispatch(validateUser(data?.JobId, newData?.email))
              .then((response) => {
                if (response?.value?.Data.Message === QUICK_APPLY_USER.ALREADY_APPLIED) {
                  return dispatch(setNotification(userExistConfig.JOB_ALREADY_APPLIED()));
                }
                if (response?.value?.Data.IsExistUser === true) {
                  setIsUserExist(true);
                  dispatch(setNotification(userExistConfig.USER_EXIST_PROCEED));
                }
                setStep(2);
                setData((prevState) => ({
                  ...prevState,
                  ...newData
                }));
                return undefined;
              })
              .catch((err) => {
                dispatch(setNotification(userExistConfig.SERVER_ERROR));
                setStep(1);
                setIsUserExist(false);
                setData((prevState) => ({
                  ...prevState,
                  ...newData
                }));
                throw err;
              });
          }}
          cancel={() => {
            setStep(1);
            close();
            localStorage.removeItem('OpenedJobId');
          }}
          data={data}
          questions={questions}
          userDataExist={userDataExist}
          isUserExist={isUserExist}
          restoredFromSession={Boolean(restoredDetails)}
          jobTitle={job?.JobTitle}
        />
      );
    case 2:
      return (
        <UploadCV
          step={displayStep}
          totalStep={totalSteps}
          nextHandle={(newData) => {
            if (questionAvailability) {
              setStep(4);
            } else {
              setStep(3);
            }
            dispatch(removeNotification());
            setData((prevState) => ({
              ...prevState,
              ...newData
            }));
          }}
          cancel={() => {
            setStep(2);
            close();
          }}
          back={() => {
            setStep(1);
          }}
          data={data}
          questions={questions}
          userDataExist={userDataExist}
          jobTitle={job?.JobTitle}
        />
      );
    case 3:
      return (
        <PreScreening
          step={displayStep}
          totalStep={totalSteps}
          data={data}
          nextHandle={(newData) => {
            setStep(4);
            setData((prevState) => ({
              ...prevState,
              ...newData,
              ...questions
            }));
          }}
          backHandle={() => setStep(2)}
          cancel={() => {
            setStep(1);
            close();
          }}
          questions={questions}
          jobTitle={job?.JobTitle}
        />
      );
    case 4:
      return (
        <ReviewForm
          step={displayStep}
          questionAvailable={questionAvailability}
          totalStep={totalSteps}
          navigate={(type) => handleNavigattion(type)}
          nextHandle={async (newData) => {
            setData((prevState) => ({
              ...prevState,
              ...newData
            }));
            dispatch(
              applyJobv2({
                Email: data.email,
                FirstName: data.firstName,
                LastName: data.lastName,
                PhoneCountryCode: data.PhoneCountryCode,
                Country: data.countryName,
                IsManageConcent: data.IsManageConcent,
                JobId: data.JobId,
                Phone: data.Phone,
                Answer1: handleAnswers(1),
                Answer2: handleAnswers(2),
                Answer3: handleAnswers(3),
                Cv: data?.Cv,
                CoverLetter: data?.CoverLetter,
                Document1: data?.Document1,
                Document2: data?.Document2,
                Document3: data?.Document3,
                ApplicationSource: params?.utm_source || '',
                ApplicationSourceDrillDown1: params?.utm_medium || '',
                ApplicationSourceDrillDown2: params?.utm_campaign || '',
                // eslint-disable-next-line no-underscore-dangle -- _hsenc is a HubSpot URL query param name
                HubSpotCampaign: params?._hsenc || '',
                PageUrl: document.URL || '',
                PageTitle: job?.JobTitle || ''
              })
            );
          }}
          cancel={() => {
            setStep(1);
            close();
          }}
          back={() => {
            if (questionAvailability) {
              setStep(2);
            } else {
              setStep(3);
            }
          }}
          data={data}
          questions={questions}
          userDataExist={userDataExist}
          jobTitle={job?.JobTitle}
          setConsentChecked={setConsentChecked}
        />
      );
    case 5:
      return (
        <FinishForm
          data={data}
          step={displayStep}
          totalStep={totalSteps}
          cancel={() => {
            setStep(1);
            close();
          }}
          isUserExist={isUserExist}
          jobTitle={job?.JobTitle}
        />
      );
    case 'error':
      return <ErrorForm error={error} cancel={close} backHandle={() => setStep(1)} />;
    default:
      return null;
  }
}
export default QuickApply;
